import { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { GlassCard } from '../ui/GlassCard';
import { Terminal, Pause, Play, Download, Radio } from 'lucide-react';
import { cn, formatUKTime } from '../../lib/utils';
import type { CallEvent } from '../../hooks/useMetricsData';

// ─── Event Type Config ───────────────────────────────────────────────

const eventTypeConfig: Record<CallEvent['type'], { color: string; label: string; bg: string }> = {
  stt: { color: 'text-info', label: 'STT', bg: 'bg-info/5' },
  agent: { color: 'text-success', label: 'AGENT', bg: 'bg-success/5' },
  deflection: { color: 'text-vokentra', label: 'DEFLECT', bg: 'bg-vokentra/5' },
  tts: { color: 'text-warning', label: 'TTS', bg: 'bg-warning/5' },
  new_call: { color: 'text-text-primary', label: 'CALL', bg: 'bg-white/5' },
  network: { color: 'text-error', label: 'NET', bg: 'bg-error/5' },
  resolution: { color: 'text-success', label: 'RESOLVE', bg: 'bg-success/5' },
};

// ─── Stream Row ──────────────────────────────────────────────────────

function StreamRow({ event, index }: { event: CallEvent; index: number }) {
  const cfg = eventTypeConfig[event.type];
  const timestamp = new Date(event.timestamp);
  const timeStr = formatUKTime(timestamp);

  return (
    <motion.div
      initial={{ opacity: 0, x: -20, height: 0 }}
      animate={{ opacity: 1, x: 0, height: 'auto' }}
      exit={{ opacity: 0, x: 20, height: 0 }}
      transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
      className={cn(
        'flex items-start gap-2 px-3 py-1.5 text-xs font-mono border-l-2 transition-colors duration-200 hover:bg-white/[0.02]',
        cfg.bg,
        event.type === 'stt' && 'border-l-info',
        event.type === 'agent' && 'border-l-success',
        event.type === 'deflection' && 'border-l-vokentra',
        event.type === 'tts' && 'border-l-warning',
        event.type === 'new_call' && 'border-l-text-muted',
        event.type === 'network' && 'border-l-error',
        event.type === 'resolution' && 'border-l-success',
      )}
    >
      <span className="text-text-muted tabular-nums shrink-0 w-[60px]">
        {timeStr}
      </span>
      <span className={cn('text-[9px] font-bold px-1 rounded shrink-0', cfg.color, 'bg-white/5')}>
        {cfg.label}
      </span>
      <span className="text-text-secondary break-all">{event.message}</span>
    </motion.div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────

interface MetricsStreamProps {
  events: CallEvent[];
  className?: string;
}

export function MetricsStream({ events, className }: MetricsStreamProps) {
  const [paused, setPaused] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const wasAtBottom = useRef(true);

  // Auto-scroll to top (newest events at top)
  useEffect(() => {
    if (scrollRef.current && !paused) {
      const el = scrollRef.current;
      // Check if user was near top (viewing newest)
      if (wasAtBottom.current) {
        el.scrollTop = 0;
      }
    }
  }, [events, paused]);

  const handleScroll = useCallback(() => {
    if (scrollRef.current) {
      const el = scrollRef.current;
      wasAtBottom.current = el.scrollTop < 50;
    }
  }, []);

  // Export to CSV
  const exportCSV = useCallback(() => {
    const headers = ['Timestamp', 'Type', 'Message', 'Details'];
    const rows = events.map((e) => [
      new Date(e.timestamp).toISOString(),
      e.type,
      e.message,
      JSON.stringify(e.detail || {}),
    ]);
    const csv = [headers, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `metrics-stream-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [events]);

  // Stats
  const eventCounts = events.reduce(
    (acc, e) => {
      acc[e.type] = (acc[e.type] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  return (
    <GlassCard
      variant="elevated"
      className={cn('flex flex-col', className)}
      header={
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-2">
            <Terminal className="w-4 h-4 text-vokentra" />
            <span className="text-sm font-semibold text-text-primary">
              Real-Time Metrics Stream
            </span>
            {!paused && (
              <span className="flex items-center gap-1 text-[10px] text-success">
                <Radio className="w-3 h-3 animate-pulse" />
                LIVE
              </span>
            )}
            {paused && (
              <span className="text-[10px] text-warning font-medium">PAUSED</span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPaused(!paused)}
              className="p-1.5 rounded-lg hover:bg-white/[0.06] text-text-muted hover:text-text-primary transition-colors"
              title={paused ? 'Resume' : 'Pause'}
            >
              {paused ? (
                <Play className="w-3.5 h-3.5 text-success" />
              ) : (
                <Pause className="w-3.5 h-3.5" />
              )}
            </button>
            <button
              onClick={exportCSV}
              className="p-1.5 rounded-lg hover:bg-white/[0.06] text-text-muted hover:text-text-primary transition-colors"
              title="Export to CSV"
            >
              <Download className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      }
    >
      {/* Mini stats bar */}
      <div className="flex items-center gap-3 px-3 py-2 border-b border-white/[0.06] overflow-x-auto">
        {Object.entries(eventTypeConfig).map(([type, cfg]) => (
          <div key={type} className="flex items-center gap-1 shrink-0">
            <span className={cn('w-1.5 h-1.5 rounded-full', cfg.color.replace('text-', 'bg-'))} />
            <span className="text-[9px] text-text-muted uppercase">{cfg.label}</span>
            <span className="text-[9px] text-text-primary tabular-nums">
              {eventCounts[type] || 0}
            </span>
          </div>
        ))}
        <div className="ml-auto text-[9px] text-text-muted tabular-nums">
          {events.length} events
        </div>
      </div>

      {/* Stream */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className={cn(
          'flex-1 overflow-y-auto min-h-[300px] max-h-[400px] scrollbar-thin py-1',
          paused && 'opacity-70'
        )}
      >
        <AnimatePresence initial={false}>
          {events.slice(0, 50).map((event, index) => (
            <StreamRow key={event.id} event={event} index={index} />
          ))}
        </AnimatePresence>

        {events.length === 0 && (
          <div className="flex items-center justify-center h-full text-text-muted text-sm">
            Waiting for events...
          </div>
        )}
      </div>
    </GlassCard>
  );
}

export default MetricsStream;
